import axios from 'axios';

const OPENWEATHER_API_KEY = import.meta.env.VITE_OPENWEATHER_API_KEY;
const BASE_URL = 'https://api.openweathermap.org/data/2.5';

// Debug: Log environment variables on load
console.log('🔑 Environment Variables Check:');
console.log('VITE_OPENWEATHER_API_KEY:', OPENWEATHER_API_KEY ? `${OPENWEATHER_API_KEY.substring(0, 10)}...` : '❌ UNDEFINED');
console.log('All VITE env vars:', Object.keys(import.meta.env).filter(k => k.startsWith('VITE_')));

class WeatherService {
  /**
   * Get current weather for a location
   */
  async getCurrentWeather(lat, lon) {
    try {
      console.log('Fetching weather for:', { 
        lat, 
        lon, 
        apiKey: OPENWEATHER_API_KEY ? `${OPENWEATHER_API_KEY.substring(0, 10)}...` : '❌ UNDEFINED',
        fullKey: OPENWEATHER_API_KEY
      });
      
      const response = await axios.get(`${BASE_URL}/weather`, {
        params: {
          lat,
          lon,
          appid: OPENWEATHER_API_KEY,
          units: 'metric',
        },
      });

      console.log('Weather API response:', response.data);
      const formatted = this.formatCurrentWeather(response.data);
      console.log('Formatted weather:', formatted);
      return formatted;
    } catch (error) {
      console.error('Error fetching current weather:', error.response?.data || error.message);
      throw new Error(`Failed to fetch current weather data: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Get 5-day forecast with 3-hour intervals
   */
  async getForecast(lat, lon) {
    try {
      const response = await axios.get(`${BASE_URL}/forecast`, {
        params: {
          lat,
          lon,
          appid: OPENWEATHER_API_KEY,
          units: 'metric',
        },
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching forecast:', error);
      throw new Error('Failed to fetch forecast data');
    }
  }

  /**
   * Get hourly forecast (using forecast endpoint)
   */
  async getHourlyForecast(lat, lon) {
    const forecast = await this.getForecast(lat, lon);
    return forecast.list.slice(0, 24); // Return next 24 hours (8 data points = 24 hours)
  }

  /**
   * Format current weather data
   */
  formatCurrentWeather(data) {
    return {
      location: {
        name: data.name,
        country: data.sys.country,
        lat: data.coord.lat,
        lon: data.coord.lon,
      },
      current: {
        temp: Math.round(data.main.temp),
        feelsLike: Math.round(data.main.feels_like),
        humidity: data.main.humidity,
        pressure: data.main.pressure,
        windSpeed: data.wind.speed,
        windDeg: data.wind.deg,
        clouds: data.clouds.all,
        visibility: data.visibility,
        description: data.weather[0].description,
        icon: data.weather[0].icon,
        main: data.weather[0].main,
      },
      timestamp: new Date(data.dt * 1000),
      sunrise: new Date(data.sys.sunrise * 1000),
      sunset: new Date(data.sys.sunset * 1000),
    };
  }

  /**
   * Calculate heat index
   */
  calculateHeatIndex(temp, humidity) {
    const T = temp * 9/5 + 32; // Convert to Fahrenheit
    const RH = humidity;

    const HI = -42.379 + 2.04901523 * T + 10.14333127 * RH - 0.22475541 * T * RH -
               0.00683783 * T * T - 0.05481717 * RH * RH + 0.00122874 * T * T * RH +
               0.00085282 * T * RH * RH - 0.00000199 * T * T * RH * RH;

    return (HI - 32) * 5/9; // Convert back to Celsius
  }

  /**
   * Calculate risk score for outdoor activities
   */
  calculateOutdoorRiskScore(weatherData) {
    let riskScore = 0;

    // Precipitation risk (0-40 points)
    if (weatherData.rain) {
      riskScore += Math.min((weatherData.rain['1h'] || 0) * 10, 40);
    }

    // Wind risk (0-25 points)
    if (weatherData.wind.speed > 10) {
      riskScore += Math.min((weatherData.wind.speed - 10) * 5, 25);
    }

    // Temperature extremes (0-20 points)
    const temp = weatherData.main.temp;
    if (temp < 5 || temp > 35) {
      riskScore += Math.min(Math.abs(temp - 20) * 2, 20);
    }

    // Humidity discomfort (0-15 points)
    const humidity = weatherData.main.humidity;
    if (humidity > 80 || humidity < 20) {
      riskScore += Math.min(Math.abs(humidity - 50) / 3, 15);
    }

    return Math.min(Math.round(riskScore), 100);
  }

  /**
   * Get weather alerts (if available)
   */
  async getWeatherAlerts(lat, lon) {
    try {
      const response = await axios.get(`${BASE_URL}/onecall`, {
        params: {
          lat,
          lon,
          appid: OPENWEATHER_API_KEY,
          exclude: 'minutely,hourly,daily',
        },
      });

      return response.data.alerts || [];
    } catch (error) {
      console.error('Error fetching weather alerts:', error);
      return [];
    }
  }

  /**
   * Search location by name
   */
  async searchLocation(query) {
    try {
      const response = await axios.get('https://api.openweathermap.org/geo/1.0/direct', {
        params: {
          q: query,
          limit: 5,
          appid: OPENWEATHER_API_KEY,
        },
      });

      return response.data.map(location => ({
        name: location.name,
        country: location.country,
        state: location.state,
        lat: location.lat,
        lon: location.lon,
        displayName: `${location.name}${location.state ? ', ' + location.state : ''}, ${location.country}`,
      }));
    } catch (error) {
      console.error('Error searching location:', error);
      throw new Error('Failed to search location');
    }
  }

  /**
   * Get reverse geocoding (location name from coordinates)
   */
  async reverseGeocode(lat, lon) {
    try {
      const response = await axios.get('https://api.openweathermap.org/geo/1.0/reverse', {
        params: {
          lat,
          lon,
          limit: 1,
          appid: OPENWEATHER_API_KEY,
        },
      });

      if (response.data.length > 0) {
        const location = response.data[0];
        return {
          name: location.name,
          country: location.country,
          state: location.state,
          lat: location.lat,
          lon: location.lon,
        };
      }
      return null;
    } catch (error) {
      console.error('Error reverse geocoding:', error);
      return null;
    }
  }
}

export const weatherService = new WeatherService();
